import { useState } from "react";
import { Check, Copy, Share2, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const SITE = "https://www.kickstartkenyafootball.com";

// The card the reader will actually see is the one the backend draws, so preview that rather than a mock,
// and always share the production URL — a preview host would give them a link that dies.
const TARGETS = [
  ["WhatsApp", (t, u) => `https://wa.me/?text=${encodeURIComponent(`${t} ${u}`)}`, true],
  ["X", (t, u) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(t)}&url=${encodeURIComponent(u)}`],
  ["Facebook", (t, u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`],
  ["LinkedIn", (t, u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}`],
];

export const ShareCard = ({ title, text, card, label = "Share", testId = "share", path }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  // `path` lets a page share its own query string (a lease comparison is only meaningful with its parameters).
  const url = path ? `${SITE}${path}`
    : typeof window === "undefined" ? SITE : `${SITE}${window.location.pathname}`;
  const message = text || title;

  const open_ = async () => {
    if (navigator.share) {
      // Share the card as a file where the device allows it — that is what puts the picture, not a browser
      // icon, into the iOS and Android share sheets.
      try {
        if (card && navigator.canShare) {
          const res = await fetch(card, { mode: "cors" });
          const blob = await res.blob();
          const file = new File([blob], "kickstartkenyafootball.png", { type: blob.type || "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ title, text: `${message} ${url}`, files: [file] });
            return;
          }
        }
      } catch {
        // fall through to the plain link share
      }
      try {
        await navigator.share({ title, text: message, url });
        return;
      } catch {
        return; // the reader dismissed the system sheet
      }
    }
    setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${message} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setFailed(true);
      setTimeout(() => setFailed(false), 3200);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={open_}
        data-testid={`${testId}-btn`}
        className="inline-flex items-center gap-2 border border-white/25 px-5 py-3 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-white/80 transition-colors hover:border-maasai hover:text-ochre"
      >
        <Share2 size={14} /> {label}
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[130] flex items-center justify-center overflow-y-auto bg-black px-5 py-10"
            onClick={() => setOpen(false)}
            data-testid={`${testId}-modal`}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[560px]"
            >
              <div className="flex items-center justify-between pb-4">
                <p className="text-[0.62rem] font-bold uppercase tracking-[0.28em] text-ochre">
                  This is what they will see
                </p>
                <button
                  type="button"
                  aria-label="Close share card"
                  onClick={() => setOpen(false)}
                  data-testid={`${testId}-close`}
                  className="p-1 text-white/60 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>

              {card ? (
                <img
                  src={card}
                  alt={`${title} share card`}
                  className="w-full border border-ochre/40"
                  data-testid={`${testId}-visual`}
                />
              ) : null}

              <p className="mt-4 text-xs leading-relaxed text-zinc-400" data-testid={`${testId}-text`}>
                {message}
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={copy}
                  data-testid={`${testId}-copy`}
                  className="inline-flex items-center justify-center gap-2 border border-white/25 px-4 py-3 text-[0.66rem] font-bold uppercase tracking-[0.16em] text-white/85 transition-colors hover:border-maasai hover:text-ochre"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}{" "}
                  {copied ? "Copied" : failed ? "Copy blocked" : "Copy link"}
                </button>
                {TARGETS.map(([name, build, primary]) => (
                  <a
                    key={name}
                    href={build(message, url)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`${testId}-${name.toLowerCase()}`}
                    className={`inline-flex items-center justify-center gap-2 px-4 py-3 text-[0.66rem] font-bold uppercase tracking-[0.16em] transition-colors ${
                      primary
                        ? "bg-ochre text-black hover:bg-white"
                        : "border border-white/25 text-white/85 hover:border-maasai hover:text-ochre"
                    }`}
                  >
                    {name}
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};
